import { useNavigate, useLocation } from "react-router-dom";
import {
  Table,
  useBreakpoints,
  Row,
  TruncatedAccount,
  MenuButton,
} from "@narmi/design_system";
import { catcat as cc } from "cerulean";
import { useLocalization } from "@fluent/react";
import {
  FormattedBulkPayment,
  hasAnyReversePermission,
  showCancelOption,
  showReverseOption,
} from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import { showAmountStrikeThrough } from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import {
  useDualApprovalPermission,
  useACHReverseCollectPermission,
  useACHReverseSendPermission,
} from "byzantine/src/dbbl/businessLogic/modules/achPayments/permissions";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";
import { FILTER_CONFIG, FilterChips } from "../Filters/filterConfig";
import {
  PAYMENT_ACTIVITY_ROUTE,
  getBulkReversalSelectionRoute,
  getBulkReverseRoute,
  getBulkReverseFromDrawerRoute,
} from "../routes";
import { RenderDrawerProps, StatusTag, TableWithDrawer } from "../common";
import styles from "../PaymentActivity.module.scss";
import BulkPaymentDrawer from "./BulkPaymentDrawer";

const BulkPaymentTable = ({
  payments,
  handleToggleCancelDialog,
}: {
  payments: FormattedBulkPayment[];
  handleToggleCancelDialog: (uuid: API.BulkPaymentId) => void;
}) => {
  const { m, l } = useBreakpoints();
  const location = useLocation();
  const navigate = useNavigate();
  const { l10n } = useLocalization();
  const institution = useInstitutionSettings();
  const hasDualApprovalPermission = useDualApprovalPermission();
  const hasReverseSendPermission = useACHReverseSendPermission();
  const hasReverseCollectPermission = useACHReverseCollectPermission();
  const isReversalFeatureEnabled = Boolean(
    institution.features?.user_initiated_ach_reversals
  );

  const handleOpenDrawer = (uuid: string) => {
    navigate(`/${PAYMENT_ACTIVITY_ROUTE}/bulk/${uuid}${location.search}`);
  };

  const renderRow = (item: FormattedBulkPayment) => {
    const {
      uuid,
      title,
      displayStatus,
      institutionAccount,
      netTotalDollarsWithSign,
      paymentCount,
      dualApprovalId,
      dateRange,
      isExpedited,
      hasReversal,
    } = item;
    const showCancel = showCancelOption(item);
    const showStrikeThrough = showAmountStrikeThrough(displayStatus);
    const hasReversePermission = hasAnyReversePermission(
      {
        hasCollectPayments: (item.paymentDebitCount ?? 0) > 0,
        hasSendPayments: (item.paymentCreditCount ?? 0) > 0,
      },
      { hasReverseCollectPermission, hasReverseSendPermission }
    );
    const showReverseButtons = showReverseOption(item, {
      isReversalFeatureEnabled,
      hasReversePermission,
    });
    const menuItems = [];

    if (dualApprovalId && hasDualApprovalPermission) {
      menuItems.push(
        <MenuButton.Item
          key="view-approvals-button"
          label={l10n.getString("label-view-approvals")}
          startIcon="eye"
          onSelect={() =>
            window.location.assign(
              `/approvals/pending?dualApprovalId=${dualApprovalId}`
            )
          }
        />
      );
    } else if (paymentCount > 0) {
      menuItems.push(
        <MenuButton.Item
          key="view-payments-button"
          label={l10n.getString("view-payments-cta", {
            total: paymentCount,
          })}
          startIcon="eye"
          onSelect={() => {
            navigate({
              pathname: `/${PAYMENT_ACTIVITY_ROUTE}`,
              search: `?${
                FILTER_CONFIG[FilterChips.BULK_PAYMENT].queryKey
              }=${uuid}`,
            });
          }}
        />
      );
    }

    if (showReverseButtons) {
      menuItems.push(
        <MenuButton.Item
          key="reverse-all-eligible-button"
          label={l10n.getString("label-reverse-all-eligible")}
          startIcon="rotate-ccw"
          onSelect={() => {
            navigate(getBulkReverseRoute(uuid));
          }}
        />,
        <MenuButton.Item
          key="select-for-reversal-button"
          label={l10n.getString("label-select-for-reversal")}
          startIcon="mouse-pointer"
          onSelect={() => {
            navigate(getBulkReversalSelectionRoute(uuid));
          }}
        />
      );
    }

    if (showCancel) {
      menuItems.push(
        <MenuButton.Item
          key="delete-button"
          label={l10n.getString("label-cancel-payment")}
          startIcon="trash-2"
          onSelect={() => {
            handleToggleCancelDialog(uuid as API.BulkPaymentId);
          }}
        />
      );
    }

    return (
      <Table.Row
        key={`table-row__${uuid}`}
        onRowClick={() => handleOpenDrawer(uuid)}
      >
        <Table.Cell>
          <Row>
            {!(m || l) ? (
              <Row.Item shrink>
                <StatusTag status={displayStatus} isBadge />{" "}
              </Row.Item>
            ) : null}
            <Row.Item>
              <div
                className={cc(styles.ellipsisWrapper, { "margin--left--l": m })}
              >
                <span>{title}</span>
                {hasReversal && (
                  <span className="margin--left--xs narmi-icon-rotate-ccw fontColor--secondary" />
                )}
                <div className="fontSize--s fontColor--secondary">
                  {dateRange}
                  {isExpedited && (
                    <span className="margin--left--xs narmi-icon-zap" />
                  )}
                </div>
              </div>
            </Row.Item>
          </Row>
        </Table.Cell>
        <Table.Cell>
          <StatusTag status={displayStatus} isBadge={false} />
        </Table.Cell>
        <Table.Cell>
          <TruncatedAccount
            name={institutionAccount?.name ?? ""}
            lastFour={institutionAccount?.getMaskedNumber() ?? ""}
          />
        </Table.Cell>
        <Table.Cell textAlign={"right" as any}>
          <span className={cc({ [styles.strikeThrough]: showStrikeThrough })}>
            {netTotalDollarsWithSign}
          </span>
        </Table.Cell>
        <Table.Cell>
          <div
            style={{ alignSelf: "end" }}
            onClick={(e) => e.stopPropagation()}
          >
            {menuItems.length > 0 && (
              <MenuButton triggerIcon="more-vertical">{menuItems}</MenuButton>
            )}
          </div>
        </Table.Cell>
      </Table.Row>
    );
  };

  const renderDrawer: RenderDrawerProps<FormattedBulkPayment> = (
    payment,
    paymentId,
    { onClose, onNext, onPrev, isOpen }
  ) => (
    <BulkPaymentDrawer
      isOpen={isOpen}
      handleClose={onClose}
      handleOnNext={onNext}
      handleOnPrev={onPrev}
      handleToggleCancelDialog={handleToggleCancelDialog}
      payment={payment}
      paymentUuid={paymentId as API.BulkPaymentId}
      handleReverseAllEligible={(uuid) =>
        navigate(getBulkReverseFromDrawerRoute(uuid))
      }
      handleSelectForReversal={(uuid) =>
        navigate(getBulkReversalSelectionRoute(uuid))
      }
    />
  );

  return (
    <TableWithDrawer
      items={payments}
      renderRow={renderRow}
      renderDrawer={renderDrawer}
      basePath={`${PAYMENT_ACTIVITY_ROUTE}/bulk`}
      paramKey="bulkPaymentId"
    />
  );
};

export default BulkPaymentTable;
